import { createFileRoute, notFound, redirect } from "@tanstack/react-router";

import { BASE_URL, OG_VERSION } from "@/common/links";
import { FeaturePage } from "@/landing/components/FeaturePage";
import {
  CLI_FEATURE_COPY_MAP,
  FEATURE_410S,
  FEATURE_NOINDEX_SLUGS,
  FEATURE_REDIRECTS,
} from "@/landing/lib/cli-feature-copy";

export const Route = createFileRoute("/_landing/feature/$slug")({
  loader: ({ params }) => {
    if (FEATURE_410S.has(params.slug)) throw notFound();

    const redirectTo = FEATURE_REDIRECTS[params.slug];
    if (redirectTo) throw redirect({ to: redirectTo, statusCode: 301 });

    const featureCopy = CLI_FEATURE_COPY_MAP[params.slug];
    if (!featureCopy) throw notFound();
    return { featureCopy, slug: params.slug };
  },
  head: ({ loaderData }) => {
    const copy = loaderData?.featureCopy;
    const url = `${BASE_URL}/feature/${loaderData?.slug}`;
    const image = `${BASE_URL}/og/feature/${loaderData?.slug}.png?v=${OG_VERSION}`;
    const description = copy?.description ?? copy?.sub.join(" ");

    return {
      meta: [
        { title: copy?.metaTitle },
        { name: "description", content: description },
        { property: "og:title", content: copy?.metaTitle },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { name: "twitter:title", content: copy?.metaTitle },
        { name: "twitter:description", content: description },
        { property: "og:image", content: image },
        { name: "twitter:image", content: image },
        ...(FEATURE_NOINDEX_SLUGS.has(loaderData?.slug ?? "")
          ? [{ name: "robots", content: "noindex,follow" }]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Wonda",
            applicationCategory: "BusinessApplication",
            operatingSystem: "macOS, Linux, Windows",
            description,
            url,
            image,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: copy?.faq.map((item) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: { "@type": "Answer", text: item.answer },
            })),
          }),
        },
      ],
    };
  },
  component: FeatureRoute,
});

function FeatureRoute() {
  const { featureCopy } = Route.useLoaderData();
  return <FeaturePage copy={featureCopy} />;
}
